import React, { useState, useRef, useEffect } from 'react';
import { Image, Table } from 'antd';
import annotation from '../../images/detail/annotation.png';
import background1 from '../../images/detail/background1.png';
import background2 from '../../images/detail/background2.png';
import cloudCenterRed from '../../images/detail/cloud-center-red.png';
import cloudCenterBlue from '../../images/detail/cloud-center-blue.png';
import cloud from '../../images/detail/cloud.png';
import tableware from '../../images/detail/tableware.png';
import card from '../../images/detail/card.png';
import tongue from '../../images/detail/tongue.png';
import suitability from '../../images/detail/suitability.png';
import taboos from '../../images/detail/taboos.png';
import summary from '../../images/detail/identification-summary.png';
import identificationIcon from '../../images/detail/identification-icon.png';
import './DetailTabs.css';
import ColorBar from '../tongue/ColorBar';
import { contitutionType, suggestionsType } from '../const';

const DetailTabs: React.FC<any> = ({ activeTab, height, data, mediaDataMap, getImageSource }) => {
  const tongueColors = ['#EDE7DB', '#FFCECE', '#FF7474', '#EE0D16', '#9A38BD'];
  const tongueColorsName = ['淡白', '淡红', '红', '绛红', '青紫'];
  const coatingColors = ['#EDE7DB', '#C49347', '#928989'];
  const coatingColorsName = ['淡白', '黄色', '灰黑'];
  const [displayHeight, setDisplayHeight] = useState<string | number>('auto');
  const [tabHeights, setTabHeights] = useState<Record<string, string | number>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  // 根据内容实际高度和配置的百分比计算显示高度
  useEffect(() => {
    // 检查是否已有当前tab的高度缓存
    if (tabHeights[activeTab]) {
      setDisplayHeight(tabHeights[activeTab]);
      return;
    }

    // 重置显示高度，确保重新计算
    setDisplayHeight('auto');

    if (height && typeof height === 'string' && height.includes('%')) {
      // 等待内容渲染完成
      const timer = setTimeout(() => {
        if (contentRef.current) {
          // 先重置容器高度，确保正确计算实际内容高度
          contentRef.current.style.height = 'auto';
          // 获取内容实际高度
          const actualHeight = contentRef.current.scrollHeight;
          // 解析百分比值
          const percent = value ? parseFloat(height) : 100;
          // 计算显示高度
          const calculatedHeight = (actualHeight * percent) / 100;
          // 确保计算出的高度不小于最小高度
          const finalHeight = Math.max(calculatedHeight, 300);
          // 存储当前tab的高度
          setTabHeights(prev => ({ ...prev, [activeTab]: finalHeight }));
          setDisplayHeight(finalHeight);
        }
      }, 500);

      return () => clearTimeout(timer);
    } else {
      // 非百分比值直接使用并存储
      const finalHeight = height || 'auto';
      setTabHeights(prev => ({ ...prev, [activeTab]: finalHeight }));
      setDisplayHeight(finalHeight);
    }
  }, [activeTab, height, data, tabHeights]); // 当tab或数据变化时重新计算

  const value = data?.resultData?.parsingResult?.diagnosis || data?.pulseDetail || data?.resultData;
  const tongueFeatures: any = () => {
    const features = data?.resultData?.checkResult?.tongueFeatures || [];
    const tongue = data?.resultData?.parsingResult?.tongue || [];

    let tongueColor: any[] = [];
    let tongueShape: any[] = [];
    let coatingColor: any[] = [];
    let coatingQuality: any[] = [];

    let foundCoatingColor = false; // 是否已找到第一个“苔色”

    for (let i = 0; i < features.length; i++) {
      const feature = features[i];

      // 在 tongue 中查找匹配的 { contentName } 对象
      const matchedItem = tongue.find((item: any) => item.contentName === feature);

      if (i === 0) {
        // 第一个是舌色
        if (matchedItem) {
          tongueColor.push(matchedItem);
        }
      } else if (!foundCoatingColor && feature.endsWith("苔")) {
        // 找到第一个以“苔”结尾的作为苔色
        if (matchedItem) {
          coatingColor.push(matchedItem);
          foundCoatingColor = true;
        }
      } else {
        if (!foundCoatingColor) {
          // 苔色未出现前的是舌形
          if (matchedItem) {
            tongueShape.push(matchedItem);
          }
        } else {
          // 苔色之后的是苔质
          if (matchedItem) {
            coatingQuality.push(matchedItem);
          }
        }
      }
    }

    return {
      "tongueColor": tongueColor,
      "tongueShape": tongueShape,
      "coatingColor": coatingColor,
      "coatingQuality": coatingQuality,
    };
  };

  // 体质检测数据
  const constitutionFeatures: any = () => {
    const master = data?.resultData?.checkFinalResult?.master?.name || '';
    const slave = data?.resultData?.checkFinalResult?.slave?.name || '';
    const constitutions = data?.resultData?.parsingResult?.constitutions || [];
    const suggestions = data?.resultData?.parsingResult?.suggestions || [];

    const masterConstitutions = constitutions.flat().find((item: any) => item.contentName === master);
    const slaveConstitutions = constitutions.flat().find((item: any) => item.contentName === slave);
    const masterSuggestions = suggestions.flat().find((item: any) => item.contentName === master);
    const slaveSuggestions = suggestions.flat().find((item: any) => item.contentName === slave);

    return {
      "master": master,
      "slave": slave,
      "masterConstitutions": masterConstitutions || [],
      "slaveConstitutions": slaveConstitutions || [],
      "masterSuggestions": masterSuggestions || [],
      "slaveSuggestions": slaveSuggestions || [],
    };
  };

  // AI识菜数据
  const foodRecognitionFeatures: any = () => {
    const FoodCategorySection = ({ title, datas }: any) => {
      if (!datas || datas.length === 0) return null;
      return (
        <div>
          <div className="title" style={{ marginTop: 30 }}>{title}</div>
          <div className="box-container">
            <div className="tongue-content-wrapper">
              <div style={{ color: '#333333', fontSize: 15, fontWeight: 500, textAlign: 'center', letterSpacing: '1px', marginBottom: '18px' }}>
                {datas?.food_list?.map((item: any) => item.food_name).join(' + ') || '未知'}
              </div>
              {datas?.food_list.map((item: any) => (
                <div
                  key={item.food_name}
                  className="tongue-content"
                  style={{ marginBottom: 20 }}
                >
                  <div style={{ color: '#94724B', fontSize: 16, fontWeight: 500, marginBottom: 10 }}>
                    {item.food_name || '未知'}
                  </div>
                  <div style={{ color: '#666666', fontSize: 15, lineHeight: '24px', letterSpacing: '1px' }}>
                    <span style={{ color: '#333333' }}>特点: </span>
                    <span dangerouslySetInnerHTML={{ __html: removePTags(item.description) }}></span>
                  </div>
                </div>
              ))}
              <div className="tongue-content" >
                <div style={{ color: '#94724B', fontSize: 16, fontWeight: 500, marginBottom: 10 }}>
                  总体建议
                </div>
                <div style={{ color: '#333333', fontSize: 15, fontWeight: 500, lineHeight: '24px', letterSpacing: '1px' }}>
                  <span dangerouslySetInnerHTML={{ __html: removePTags(datas.general_advice) }}></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    const SpecialHighlight = ({ title, datas }: any) => {
      if (!datas || datas.length === 0) return null;
      return (
        <div>
          <div className="title" style={{ marginTop: 30 }}>{title}</div>
          <div className="box-container">
            <div className="tongue-content-wrapper">
              <div className="tongue-content">
                <div style={{ color: '#333333', fontSize: 15, fontWeight: 500, textAlign: 'center', letterSpacing: '1px', marginBottom: '18px' }}>
                  {datas.highlights_title}</div>
                <div style={{ color: '#666666', fontSize: 15, lineHeight: '24px', letterSpacing: '1px' }}>
                  <span dangerouslySetInnerHTML={{ __html: removePTags(datas.highlights) }}></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    const Summary = ({ datas }: any) => {
      if (!datas) return null;
      return (
        <div>
          <div style={{
            marginTop: 20,
            position: 'relative',
            textAlign: 'center',
            backgroundImage: `url(${summary})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px 0'
          }}>
            <div style={{
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '25px',
              color: '#94724B',
              textAlign: 'center',
              maxWidth: '80%',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
            }}>
              {datas || '未知'}
            </div>
          </div>
          <div style={{ marginTop: 20, position: 'relative', textAlign: 'center' }}>
            <Image
              src={identificationIcon}
              preview={false}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      );
    };

    const ConsumptionSuggestions = ({ title, datas }: any) => {
      if (!datas || datas.length === 0) return null;
      return (
        <div>
          <div className="title" style={{ marginTop: 30 }}>{title}</div>
          <div className="box-container">
            <div className="tongue-content-wrapper">
              <div style={{ color: '#333333', fontSize: 15, fontWeight: 500, textAlign: 'center', letterSpacing: '1px', marginBottom: '18px' }}>
                最近体质: {datas.constitution}</div>
              <div className="tongue-content">
                {
                  datas.consumption_suggestions?.suitable && datas.consumption_suggestions.suitable.length > 0 && (
                    <>
                      <div style={{ color: '#94724B', fontSize: 16, fontWeight: 500, marginBottom: 10 }}>宜食</div>
                      {datas.consumption_suggestions?.suitable.map((item: any) =>
                        <div
                          key={item.food_name}
                          style={{
                            color: '#666666',
                            fontSize: 15,
                            lineHeight: '24px',
                            letterSpacing: '1px',
                            marginBottom: 20
                          }}>
                          <span style={{ color: '#333333' }}>{item.food_name}: </span>
                          <span dangerouslySetInnerHTML={{ __html: removePTags(item.reason) }}></span>
                        </div>
                      )}
                    </>
                  )
                }
                {
                  datas.consumption_suggestions?.avoid && datas.consumption_suggestions.avoid.length > 0 && (
                    <>
                      <div style={{ color: '#94724B', fontSize: 16, fontWeight: 500, marginBottom: 10 }}>慎食</div>
                      {datas.consumption_suggestions?.avoid.map((item: any, index: number, array: any[]) =>
                        <div
                          key={item.food_name}
                          style={{
                            color: '#666666',
                            fontSize: 15,
                            lineHeight: '24px',
                            letterSpacing: '1px',
                            marginBottom: index === array.length - 1 ? 0 : 20
                          }}>
                          <span style={{ color: '#333333' }}>{item.food_name}: </span>
                          <span dangerouslySetInnerHTML={{ __html: removePTags(item.reason) }}></span>
                        </div>
                      )}
                    </>
                  )
                }
              </div>
            </div>
          </div>
        </div>
      );
    };

    const DietaryRecommendations = ({ title, datas }: any) => {
      if (!datas || datas.length === 0) return null;
      return (
        <div>
          <div className="title" style={{ marginTop: 30 }}>{title}</div>
          <div className="box-container">
            <div className="tongue-content-wrapper">
              <div style={{ color: '#333333', fontSize: 15, fontWeight: 500, textAlign: 'center', letterSpacing: '1px', marginBottom: '18px' }}>
                目标: {datas.recommendation_goal}</div>
              {datas.recipes.map((item: any, index: number, array: any[]) =>
                <div key={item.dish_name} className="tongue-content" style={{ marginBottom: index === array.length - 1 ? 0 : 20 }}>
                  <div style={{ color: '#94724B', fontSize: 16, fontWeight: 500, marginBottom: 10 }}>
                    {item.dish_name || '未知'}</div>
                  <div style={{ color: '#666666', fontSize: 15, lineHeight: '24px', letterSpacing: '1px', marginBottom: 10 }}>
                    <span style={{ color: '#333333' }}>做法: </span>
                    <span dangerouslySetInnerHTML={{ __html: removePTags(item.preparation) }}></span>
                  </div>
                  <div style={{ color: '#666666', fontSize: 15, lineHeight: '24px', letterSpacing: '1px' }}>
                    <span style={{ color: '#333333' }}>功效: </span>
                    <span dangerouslySetInnerHTML={{ __html: removePTags(item.efficacy) }}></span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    const NatureFlavorMeridianTropism = ({ title, datas }: any) => {
      if (!datas || datas.length === 0) return null;

      const columns: any[] = [
        {
          title: "菜品",
          dataIndex: "food_name",
          align: "center",
          search: false,
          fixed: 'left',
        },
        {
          title: "主要食材",
          dataIndex: "main_ingredients",
          align: "center",
          search: false,
        },
        {
          title: "性味归经",
          dataIndex: "nature_flavor_meridian",
          align: "center",
          search: false,
        },
        {
          title: "功效简述",
          dataIndex: "efficacy",
          align: "center",
          search: false,
        },
      ];

      return (
        <div>
          <div className="title" style={{ marginTop: 30 }}>{title}</div>
          <Table
            rowKey='food_name'
            scroll={{ x: 400 }}
            columns={columns}
            dataSource={datas}
            pagination={false}
            style={{
              borderRadius: '20px',
              overflow: 'hidden',
              border: '2px solid #DACCBB'
            }}
            className="food-recognition-table"
            onHeaderRow={() => ({
              style: {
                backgroundColor: '#FAF5EF'
              }
            })}
          />
        </div>
      );
    };

    return {
      FoodCategorySection,
      SpecialHighlight,
      Summary,
      ConsumptionSuggestions,
      DietaryRecommendations,
      NatureFlavorMeridianTropism,
    };
  };

  const removePTags = (html: string): string => {
    if (!html) return '';
    return html.replace(/<p>/g, '').replace(/<\/p>/g, '').replace(/\n/g, '<br/>');
  };

  // 舌诊诊断报告内容
  const renderTongueReport = () => (
    <div className="tongue-report-content">
      <div style={{ marginTop: 20 }}>
        <div className="box-container" style={{ marginBottom: 10 }}>
          <div className="tongue-content-wrapper"
            style={{
              backgroundImage: `url(${background2})`,
              backgroundPosition: 'bottom center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 30%',
              zIndex: 1,
              paddingBottom: 22,
            }}>
            <div className="tongue-title" style={{ color: '#666666' }}>诊断结果</div>
            <div style={{
              position: 'absolute',
              top: '15px',
              right: '10px',
              backgroundColor: '#E1EED8',
              color: '#4A9C41',
              padding: '4px 8px',
              borderRadius: '9px',
              fontSize: '15px',
              fontWeight: '500px',
              lineHeight: '20px',
              textAlign: 'center',
              fontStyle: 'normal',
              textTransform: 'none',
            }}>
              {value?.level}
            </div>
            <div className="tongue-content" style={{ fontWeight: 400 }}>
              <div style={{
                color: '#333333',
                fontSize: '22px',
                lineHeight: '31px',
                fontWeight: '600px',
                marginBottom: '10px',
              }}>{value?.result}</div>
              <span dangerouslySetInnerHTML={{ __html: removePTags(value?.explanation) }}></span>
            </div>
          </div>
        </div>
        <div className="title">舌象特征分析</div>
        <div className="imageBox">
          <img src={tongue}
            style={{ width: '100%', height: '100%' }}
            alt="舌象" />
        </div>
        <div className="tongue-title" style={{ color: '#666666' }}>舌色</div>
        {tongueFeatures().tongueColor.map((item: any, index: number) => (
          <div key={index}>
            <ColorBar
              colors={tongueColors}
              colorsName={tongueColorsName}
              selectedColorName={item.contentName} />
            <div key={index} className="tongue-content tongue-color">
              <span style={{ color: '#94724B', fontSize: 16, fontWeight: 500 }}>{item.contentName}：</span>
              <span dangerouslySetInnerHTML={{ __html: removePTags(item.mediaList[0].description) }}></span>
            </div>
          </div>
        ))}
      </div>
      <div className="box-container">
        <div className="tongue-content-wrapper">
          <div className="tongue-title" style={{ color: '#666666' }}>舌形</div>
          {tongueFeatures().tongueShape.map((item: any, index: number) => (
            <div key={index} className="tongue-content" style={{ marginBottom: 10 }}>
              <span style={{ color: '#94724B', fontSize: 16, fontWeight: 500 }}>{item.contentName}：</span>
              <span dangerouslySetInnerHTML={{ __html: removePTags(item.mediaList[0].description) }}></span>
            </div>
          ))}
        </div>
      </div>
      <div className="box-container" style={{ marginBottom: 10 }}>
        <div className="tongue-content-wrapper">
          <div className="tongue-title" style={{ color: '#666666' }}>苔色</div>
          {tongueFeatures().coatingColor.map((item: any, index: number) => (
            <div key={index} className="tongue-content">
              <ColorBar
                colors={coatingColors}
                colorsName={coatingColorsName}
                selectedColorName={item.contentName} />
              <span style={{ color: '#94724B', fontSize: 16, fontWeight: 500 }}>{item.contentName}：</span>
              <span dangerouslySetInnerHTML={{ __html: removePTags(item.mediaList[0].description) }}></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // 体质检测诊断报告内容
  const renderConstitutionReport = () => {
    // 定义体质特征的固定显示顺序
    const constitutionFeatureOrder = ['population_characteristics', 'common_symptom', 'disease_predisposition'];
    const { master, slave, masterConstitutions, slaveConstitutions } = constitutionFeatures();

    // 渲染单个体质特征的函数
    const renderConstitutionFeature = (mediaList: any[], fieldKey: string) => {
      const item = mediaList?.find((item: any) => item.fieldKey === fieldKey);
      if (!item) return null;

      return (
        <div key={fieldKey} className="tongue-content" style={{ marginBottom: 10, display: 'flex' }}>
          <span style={{
            letterSpacing: '1px', color: '#94724B', fontSize: 15, fontWeight: 500,
            minWidth: '70px', paddingRight: '10px', lineHeight: '24px'
          }}>{contitutionType[item.fieldKey as keyof typeof contitutionType]?.text || '未知'}</span>
          <span style={{ color: '#333333' }} dangerouslySetInnerHTML={{ __html: removePTags(item.description) }}></span>
        </div>
      );
    };

    return (
      <div>
        <div className="title">主要体质为<span className="subtitle">{master}</span></div>
        <div className="box-container">
          <div className="tongue-content-wrapper">
            {constitutionFeatureOrder.map(fieldKey =>
              renderConstitutionFeature(masterConstitutions?.mediaList, fieldKey)
            )}
          </div>
        </div>
        {
          slaveConstitutions && slaveConstitutions?.mediaList?.length > 0 && <div>
            <div className="title" style={{ marginTop: 30 }}>兼有体质为<span className="subtitle">{slave}</span></div>
            <div className="box-container">
              <div className="tongue-content-wrapper">
                {constitutionFeatureOrder.map(fieldKey =>
                  renderConstitutionFeature(slaveConstitutions?.mediaList, fieldKey)
                )}
              </div>
            </div>
          </div>
        }
      </div>
    );
  };

  // 脉诊诊断报告内容
  const renderPulseReport = () => (
    <div>
      <div className="diagnosis-video-section">
        <div className="diagnosis-video-title">{mediaDataMap['conclusion']?.description}</div>
        <div className="video-container">
          {!mediaDataMap['pulse_images'] ? (
            <div style={{
              width: '100%',
              height: '200px',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px'
            }}>
              <span style={{ color: '#999' }}>数据加载中...</span>
            </div>
          ) : (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10
              }}>
                <span className='pulse-result-title'>诊断结果：<span className='pulse-result-content'>{data?.pulseType}</span></span>
                <div className="measurement-tag">
                  <span className={`tag ${data?.measurementType === 'WATCH' ? 'watch-tag' : 'finger-tag'}`}>
                    {data?.measurementType === 'WATCH' ? '手表' : '指尖'}测量
                  </span>
                </div>
              </div>
              <Image
                src={getImageSource(mediaDataMap['pulse_images']?.pictures[0]?.pictureName)}
                alt="脉象图片"
                preview={false}
                style={{ width: '100%', marginBottom: 10, height: '200px' }}
                fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWRlZGVlIi8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuWbvueJh+WKoOi9veWksei0pTwvdGV4dD4KPC9zdmc+"
              />
            </div>
          )}
        </div>
        <div className="video-description">
          <div className="video-box" style={{ marginRight: "15px" }}>
            <div className="video-title">表现</div>
            <div className="video-content">{mediaDataMap['common_symptom']?.description}</div>
          </div>
          <div className="video-box">
            <div className="video-title">建议</div>
            <div className="video-content">{mediaDataMap['suggestion']?.description}</div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        <div className="title">
          脉象数据
          <Image
            src={annotation}
            className="image-annotation"
            style={{ width: '20px', height: '20px' }}
            preview={false}
          />
        </div>
        <div className="box-container">
          <div
            className="box"
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              backgroundImage: `url(${background1}), url(${background2})`,
              backgroundPosition: 'top right, bottom center',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundSize: 'auto 50%, auto 50%',
              position: 'relative',
              zIndex: 1
            }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div className="pulse-title">频率</div>
              <div className="pulse-content">{mediaDataMap['frequency']?.description}</div>
            </div>
            <div className="divider"></div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div className="pulse-title">脉力</div>
              <div className="pulse-content">{mediaDataMap['pulse_strength']?.description}</div>
            </div>
            <div className="divider"></div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div className="pulse-title">脉势</div>
              <div className="pulse-content">{mediaDataMap['pulse_momentum']?.description}</div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        <div className="title">
          特征
        </div>
        <div className="box-container">
          <div className="box">
            <div style={{ padding: '0 19px' }}>
              <span className="primary-title">特征：</span>
              <span className="primary-content">{data?.pulseDetail?.description}</span>
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        <div className="title">
          主病
        </div>
        <div className="box-container">
          <div className="box">
            <div style={{ padding: '0 19px' }}>
              <span className="primary-title">主病：</span>
              <span className="primary-content">{mediaDataMap['primary_indication']?.description}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // AI识菜识菜详情内容
  const renderFoodRecognitionReport = () => {
    const { FoodCategorySection, SpecialHighlight, Summary, ConsumptionSuggestions,
      DietaryRecommendations, NatureFlavorMeridianTropism } = foodRecognitionFeatures();
    return (
      <div>
        {data.foodsCount <= 4 && (
          <div>
            <ConsumptionSuggestions title="食用建议" datas={data?.suggestData} />
            <DietaryRecommendations title="饮食推荐" datas={data?.suggestData?.dietary_recommendations} />
          </div>
        )}
        <FoodCategorySection title="优质碳水" datas={data?.infoData?.carbs_food} />
        <FoodCategorySection title="高质量蛋白" datas={data?.infoData?.protein_food} />
        <FoodCategorySection title="膳食纤维" datas={data?.infoData?.fiber_food} />
        <SpecialHighlight title="特别出彩细节" datas={data?.excellentData?.chicken_soup_special_highlight} />
        {data.foodsCount <= 4 && (
          <NatureFlavorMeridianTropism title="食物性味归经" datas={data?.propertyData?.nature_flavor_meridian_tropism} />
        )}
        <Summary datas={data?.excellentData?.chicken_soup_special_highlight?.soup_for_soul} />
      </div>
    );
  };

  // 舌诊调理方案内容
  const renderTonguePlan = () => (
    <div className="tongue-plan-content">
      <div className="title">病症介绍</div>
      <div className="box-container">
        <div className="tongue-content-wrapper">
          <div className="tongue-title">病症介绍</div>
          <div className="tongue-content">{value?.symptomsIntroduction}</div>
        </div>
      </div>
      <div className="title" style={{ marginTop: 36 }}>调理建议</div>
      <div className="box-container" style={{ marginBottom: 10 }}>
        <div className="tongue-content-wrapper">
          <div className="tongue-title">食疗调理建议</div>
          <div className="tongue-content">{value?.foodTherapySuggestions}</div>
        </div>
      </div>
      <div className="box-container" style={{ marginBottom: 10 }}>
        <div className="tongue-content-wrapper"
          style={{
            backgroundImage: `url(${suitability})`,
            backgroundPosition: 'top right',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'auto 60px',
            position: 'relative',
            zIndex: 1
          }}>
          <div className="tongue-title">食疗宜食</div>
          <div className="tongue-content">{value?.foodBetter}</div>
        </div>
      </div>
      <div className="box-container">
        <div className="tongue-content-wrapper"
          style={{
            backgroundImage: `url(${taboos})`,
            backgroundPosition: 'top right',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'auto 60px',
            position: 'relative',
            zIndex: 1
          }}>
          <div className="tongue-title">食疗忌食</div>
          <div className="tongue-content">{value?.foodWorse}</div>
        </div>
      </div>
    </div>
  );

  // 体质检测调理方案内容
  const renderConstitutionPlan = () => {
    // 定义建议类型的固定显示顺序
    const suggestionOrder = ['overall_suggestion', 'emotion_regulation', 'live_regulation', 'diet_regulation'];
    const masterSuggestions = constitutionFeatures().masterSuggestions;
    const mediaList = masterSuggestions?.mediaList || [];

    // 渲染单个建议项的函数
    const renderSuggestionItem = (fieldKey: string) => {
      const items = mediaList.filter((item: any) => item.fieldKey === fieldKey);
      if (items.length === 0) return null;

      return (
        <div key={fieldKey}>
          <div className="title" style={{ marginTop: fieldKey === 'overall_suggestion' ? 0 : 30 }}>
            {suggestionsType[fieldKey as keyof typeof suggestionsType]?.text || '未知'}
          </div>
          <div className="box-container" style={{ marginBottom: 10 }}>
            <div className="tongue-content-wrapper">
              {items.map((item: any, index: number) => (
                <div key={index} className="tongue-content" style={{ marginBottom: 10 }}>
                  <div className="tongue-title">{suggestionsType[item.fieldKey as keyof typeof suggestionsType]?.text || '未知'}</div>
                  <div style={{ color: '#333333' }} dangerouslySetInnerHTML={{ __html: removePTags(item.description) }}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="tongue-plan-content">
        {suggestionOrder.map(renderSuggestionItem)}
      </div>
    );
  };

  // 脉诊调理方案内容
  const renderPulsePlan = () => (
    <div>
      <div className="title">脉象宜忌</div>
      <div className="box-container">
        <div
          className="plan-content-wrapper"
          style={{
            backgroundImage: `url(${background2})`,
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 15%',
            zIndex: 1
          }}
        >
          <div className="image-placeholder">
            <div className="center-image-container" style={{ width: '50px', height: '50px' }}>
              <Image src={cloudCenterRed} preview={false} />
              <div className="center-text">宜</div>
            </div>
            <Image src={cloud} preview={false} className="cloud-background" />
          </div>
          <div className="text-placeholder">
            {mediaDataMap["pulse_indication"]?.list?.map((item: any, index: number) => (
              <div key={index} className="preference-item">
                <span className="preference-title preference-title-red">{item.title}：</span>
                <span className="preference-content">{item.content}</span>
              </div>
            ))}
          </div>
          <div className="image-placeholder">
            <div className="center-image-container" style={{ width: '50px', height: '50px' }}>
              <Image src={cloudCenterBlue} preview={false} />
              <div className="center-text">忌</div>
            </div>
          </div>
          <div className="text-placeholder">
            {mediaDataMap["pulse_contraindication"]?.list?.map((item: any, index: number) => (
              <div key={index} className="preference-item">
                <span className="preference-title preference-title-blue">{item.title}：</span>
                <span className="preference-content">{item.content}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="title" style={{ marginTop: 36 }}>生活作息</div>
      <div className="box-container">
        <div className="plan-content-wrapper">
          {mediaDataMap["lifestyle_routine"]?.list?.map((item: any, index: number) => (
            <div key={index} className="lifestyle-item">
              <span className="lifestyle-title">{item.title}</span> - {item.content}
            </div>
          ))}
        </div>
      </div>
      <div className="title" style={{ marginTop: 36 }}>饮食建议</div>
      <div className="box-container">
        <div className={`plan-content-wrapper`}
          style={{
            backgroundImage: `url(${tableware})`,
            backgroundPosition: 'top right',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'auto 5%',
            zIndex: 1
          }}>
          <div className="diet-content-container">
            <div className="breakfast-card-container"
              style={{ backgroundImage: `url(${card})` }}>
              <div className="breakfast-card-text">
                早餐
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {mediaDataMap["diet_breakfast"]?.list?.map((item: any, index: number) => (
                <div key={index} className="diet-content-item">
                  <div className="plan-span"><span>{item.food}</span></div>
                  <div><span className="plan-span-title">食材：</span>{item.ingredients}</div>
                  <div><span className="plan-span-title-little">做法：</span>{item.instructions}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="divider-line"></div>
          <div className="diet-content-container">
            <div className="breakfast-card-container"
              style={{ backgroundImage: `url(${card})` }}>
              <div className="breakfast-card-text">
                午餐
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {mediaDataMap["diet_lunch"]?.list?.map((item: any, index: number) => (
                <div key={index} className="diet-content-item">
                  <div className="plan-span"><span>{item.food}</span></div>
                  <div><span className="plan-span-title">食材：</span>{item.ingredients}</div>
                  <div><span className="plan-span-title-little">做法：</span>{item.instructions}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // AI识菜养生建议内容
  const renderFoodRecognitionPlan = () => {
    const { ConsumptionSuggestions, DietaryRecommendations, NatureFlavorMeridianTropism } = foodRecognitionFeatures();
    return (
      <div>
        <ConsumptionSuggestions title="食用建议" datas={data?.suggestData} />
        <DietaryRecommendations title="饮食推荐" datas={data?.suggestData?.dietary_recommendations} />
        <NatureFlavorMeridianTropism title="食物性味归经" datas={data?.propertyData?.nature_flavor_meridian_tropism} />
      </div>
    );
  };

  // 报告内容映射
  const reportRenderMap: Record<string, () => React.ReactNode> = {
    'TONGUE': renderTongueReport,
    'CORPOREALITY': renderConstitutionReport,
    'PULSE': renderPulseReport,
    'FOOD_RECOGNITION': renderFoodRecognitionReport,
  };

  // 方案内容映射
  const planRenderMap: Record<string, () => React.ReactNode> = {
    'TONGUE': renderTonguePlan,
    'CORPOREALITY': renderConstitutionPlan,
    'PULSE': renderPulsePlan,
    'FOOD_RECOGNITION': renderFoodRecognitionPlan,
  };

  return (
    <div className="tab-content" style={{ height: displayHeight }} ref={contentRef}>
      {activeTab === 'report' ? (
        <div className="report-content">
          {(reportRenderMap[data?.type] || (() => renderPulseReport()))()}
        </div>
      ) : (
        <div className="plan-content" style={{ position: 'relative' }}>
          {(planRenderMap[data?.type] || (() => renderPulsePlan()))()}
        </div>
      )}
    </div>
  );
};

export default DetailTabs;